import { useCreateLineAdsRecordMutation, useCreateLineAdsTemplateMutation, useGetLineAdsTemplatesQuery, useRemoveLineAdsTemplateMutation, useUpdateLineAdsTemplateAssigneeMutation, useUpdateLineAdsTemplateMutation } from "../../../../generated/graphql";
import CreateMarkupButton from "../../markupSetting/CreateMarkupButton";
import LineAccountSelect from "../line/LineAccountSelect";
import LineCampaignSelect from "../line/LineCampaignSelect";
import { AdsPlatformAdapter, ReportListItem, MetricsGroup } from "../adapters/types";

const FieldMap: Record<string,string> = {
  customer: "帳戶",
  campaign: "廣告活動",
  adgroup: "廣告群組",
  ad: "廣告",
  gender: "性別",
  age: "年齡層",
  area: "地區",
  os: "裝置",
  placement: "版位",
  total: "總覽",
  daily: "每日",
  'daily-total': "每日總計",
  'daily-customer': "每日-帳戶",
  'daily-campaign': "每日-廣告活動",
  'daily-adgroup': "每日-廣告群組"
  // ... LINE 版位/維度/指標中文（自行補齊）
};

const reportList: ReportListItem[] = [
  { name: "customer", title: "帳戶", fields: ["total","daily","daily-total"] },
  { name: "campaign", title: "廣告活動", fields: ["total","daily"] },
  { name: "adgroup", title: "廣告群組", fields: ["total","daily"] },
  { name: "ad", title: "廣告", fields: ["total","daily"] },
  { name: "gender", title: "性別", fields: ["total","customer","campaign","adgroup","daily","daily-customer","daily-campaign","daily-adgroup"] },
  { name: "age", title: "年齡層", fields: ["total","customer","campaign","adgroup","daily","daily-customer","daily-campaign","daily-adgroup"] },
  { name: "area", title: "地區", fields: ["total","customer","campaign","adgroup","daily","daily-customer","daily-campaign","daily-adgroup"] },
  { name: "os", title: "裝置", fields: ["total","customer","campaign","adgroup","daily","daily-customer","daily-campaign","daily-adgroup"] },
  { name: "placement", title: "版位", fields: ["total","customer","campaign","adgroup","daily","daily-customer","daily-campaign","daily-adgroup"] },

  // ... 依 LINE 報表支持的切面補齊
];

const metricsGroups: MetricsGroup[] = [
  { title: "基本", fields: [
    { field: "click", label: "點擊數" },
    { field: "imp", label: "曝光數" },
    { field: "reach", label: "觸及數" },
    { field: "ctr", label: "點閱率" },
    { field: "cost", label: "花費" },
    { field: "cpc", label: "CPC 平均單次點擊成本" },
    { field: "cpm", label: "CPM 平均千次曝光成本" },
  ]},
  { title: "轉換", fields: [
    { field: "cvViewItemDetail", label: "查看商品詳情次數" },
    { field: "cvrViewItemDetail", label: "查看商品詳情轉換率" },
    { field: "cpaViewItemDetail", label: "查看商品詳情轉換成本" },
    { field: "cvAddToCart", label: "加入購物車數" },
    { field: "cvrAddToCart", label: "加入購物車轉換率" },
    { field: "cpaAddToCart", label: "加入購物車轉換成本" },
    { field: "cvInitiateCheckOut", label: "開始結帳數" },
    { field: "cvrInitiateCheckOut", label: "開始結帳轉換率" },
    { field: "cpaInitiateCheckOut", label: "開始結帳轉換成本" },
    { field: "cvGenerateLead", label: "潛在客戶數" },
    { field: "cvrGenerateLead", label: "潛在客戶轉換率" },
    { field: "cpaGenerateLead", label: "潛在客戶轉換成本" },
    { field: "cvCompleteReservation", label: "完成預約數" },
    { field: "cvrCompleteReservation", label: "完成預約轉換率" },
    { field: "cpaCompleteReservation", label: "完成預約轉換成本" },
    { field: "cvCompleteRegistration", label: "完成註冊數" },
    { field: "cvrCompleteRegistration", label: "完成註冊轉換率" },
    { field: "cpaCompleteRegistration", label: "完成註冊轉換成本" },
    { field: "cvPurchase", label: "轉換數" },
    { field: "cvrPurchase", label: "轉換率" },
    { field: "cpaPurchase", label: "轉換成本" },
    { field: "cvPurchaseValue", label: "轉換價值" },
    { field: "roas", label: "ROAS" },
  ]},
  { title: "影片", fields: [
    { field: "videoView3s", label: "影片觀看數" },
    { field: "imp/videoView3s", label: "影片觀看率" },
    { field: "videoView25r", label: "影片觀看 25%" },
    { field: "videoView50r", label: "影片觀看 50%" },
    { field: "videoView75r", label: "影片觀看 75%" },
    { field: "videoCompletions", label: "影片觀看 100%" },
    { field: "spend/videoView3s", label: "影片觀看成本" },
  ]},
  { title: "App", fields: [
    { field: "install", label: "App 安裝數" },
    { field: "installCvr", label: "安裝率" },
    { field: "open", label: "App 開啟數" },
    { field: "tutorialComplete", label: "教學完成數" },
    { field: "spend/install", label: "App 安裝成本" },
  ]},
  // ... 視 LINE 支援補強（曝光比重/影音/互動等）
];

export const lineAdapter: AdsPlatformAdapter = {
  key: 'line',
  displayName: 'LINE Ads',
  nameLabel: '報表名稱',
  tableNameHeader: 'LINE Ads 報表名稱',
  createButtonText: '新增LINE Ads報表設定',
  generateQueryEntity: 'lineAds',
  removeUnexposedLabel: '移除所有未曝光資料',

  formatField: (k) => {
    // First check FieldMap
    if (FieldMap[k]) {
      return FieldMap[k];
    }
    
    // Then check metricsGroups for metric fields
    for (const group of metricsGroups) {
      const metric = group.fields.find(field => field.field === k);
      if (metric) {
        return metric.label;
      }
    }
    
    // Return original key if no mapping found
    return k;
  },
  reportList,
  metricsGroups,

  AccountSelect: LineAccountSelect,
  CampaignSelect: LineCampaignSelect,
  MarkupButton: () => <CreateMarkupButton platform="line" />, // 有需要再接

  useGetTemplatesQuery: () => {
    const { data, loading, error } = useGetLineAdsTemplatesQuery();
    return { data: data ? { templates: data?.lineAdsTemplates! } : undefined, loading, error };
  },
  useCreateTemplateMutation: () => {
    const [mutate] = useCreateLineAdsTemplateMutation();
    return [mutate];
  },
  useUpdateTemplateMutation: () => {
    const [mutate] = useUpdateLineAdsTemplateMutation();
    return [mutate];
  },
  useRemoveTemplateMutation: () => {
    const [mutate] = useRemoveLineAdsTemplateMutation();
    return [mutate];
  },
  useCreateRecordMutation: () => {
    const [mutate] = useCreateLineAdsRecordMutation();
    return [mutate];
  },
  useUpdateTemplateAssigneeMutation: () => {
    const [mutate] = useUpdateLineAdsTemplateAssigneeMutation();
    return [mutate];
  },

  refetchTemplatesQueryName: "getLineAdsTemplates",
  refetchRecordListQueryName: "getLineAdsRecordList",
  recordInputKey: "lineAdsRecordInput",
  templateInputKey: "lineAdsTemplateInput",

  buildTemplateInput: ({ name, reportNames, metricsFields, accountCampaigns, assigneeId, removeUnexposed }) => ({
    name, reportNames, metricsFields, accountCampaigns, assigneeId, removeUnexposed
  }),
};
